import {
  campaignsReportResponseSchema,
  salesReportResponseSchema,
  workBlocksResponseSchema,
  workBlocksSettingSchema,
  type ReportsQuery,
  type WorkBlocksSwitchBody,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { reportKeys } from "./report-keys";
import { reportsQueryOf } from "./report-search";

// Le letture del Report (piano M7, Task 3, 5 e 7; D7): `GET /api/reports/sales` («Commerciali») e `GET /api/reports/campaigns`
// («Campagne») con il periodo e «Di chi» dell'indirizzo, e `GET /api/reports/work-blocks` (la giornata a blocchi di una persona in un
// giorno, letta soltanto se è accesa); l'interruttore `PUT /api/settings/work-blocks` rilegge tutto il Report. Niente cache sul
// server: il browser tiene ogni lettura per (scheda, vista, query) mezzo minuto e la rilegge al ritorno sulla pagina; la voce della
// rail legge in anticipo la vista di partenza («Mese», «Tutto il team»; per chi vede i suoi numeri, i suoi), così la pagina si apre
// subito. Nella «Guarda come…» la richiesta porta la vista (`apiRequest`), e il server risponde con i numeri della persona osservata.

/** Il percorso della lettura di una scheda, con i soli parametri che ci sono. */
function reportPath(route: "sales" | "campaigns", query: ReportsQuery): string {
  const params = new URLSearchParams({ period: query.period });
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.who !== undefined) params.set("who", query.who);
  return `/api/reports/${route}?${params.toString()}`;
}

/** «Commerciali» per la query, nella vista della scheda. */
export function salesReportQuery(tab: TabContext, query: ReportsQuery) {
  return queryOptions({
    queryKey: reportKeys.sales(tabKey(tab), commercialViewOf(tab), query),
    queryFn: ({ signal }) => apiRequest("GET", reportPath("sales", query), { schema: salesReportResponseSchema, tab, signal }),
    staleTime: 30_000,
  });
}

/** «Campagne» per la query, nella vista della scheda, con la stessa durata di «Commerciali». */
export function campaignsReportQuery(tab: TabContext, query: ReportsQuery) {
  return queryOptions({
    queryKey: reportKeys.campaigns(tabKey(tab), commercialViewOf(tab), query),
    queryFn: ({ signal }) => apiRequest("GET", reportPath("campaigns", query), { schema: campaignsReportResponseSchema, tab, signal }),
    staleTime: 30_000,
  });
}

/** La vista di partenza dell'indirizzo `/report` senza parametri: «Mese», «Tutto il team» (o, per chi vede i suoi, i suoi). */
export const DEFAULT_REPORTS_QUERY: ReportsQuery = reportsQueryOf({});

/** Legge in anticipo la vista di partenza, per la voce della rail; non rilegge una lettura ancora fresca. */
export function prefetchSalesReport(queryClient: QueryClient, tab: TabContext): Promise<void> {
  return queryClient.prefetchQuery(salesReportQuery(tab, DEFAULT_REPORTS_QUERY));
}

/** La giornata a blocchi di 30 minuti di una persona in un giorno, nella vista della scheda (Task 7), con la stessa durata di «Commerciali». */
export function workBlocksQuery(tab: TabContext, person: string, day: string) {
  return queryOptions({
    queryKey: reportKeys.workBlocks(tabKey(tab), commercialViewOf(tab), person, day),
    queryFn: ({ signal }) => apiRequest("GET", `/api/reports/work-blocks?${new URLSearchParams({ person, day }).toString()}`, { schema: workBlocksResponseSchema, tab, signal }),
    staleTime: 30_000,
  });
}

/**
 * Accende o spegne la giornata a blocchi per l'azienda della scheda (soltanto il Super admin di Gioia365, Task 7). Dopo, si rilegge il
 * Report (lo stato e la versione stanno in «Commerciali») e le Impostazioni → «Obiettivi», che hanno la stessa versione.
 */
export function useSwitchWorkBlocks(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkBlocksSwitchBody) => apiRequest("PUT", "/api/settings/work-blocks", { schema: workBlocksSettingSchema, body, tab }),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportKeys.all(tabKey(tab)) }),
        queryClient.invalidateQueries({ queryKey: ["scheda", tabKey(tab), "report-goals"] }),
      ]);
    },
  });
}
